import { useEffect, useMemo, useState } from "react";

const getPrefersReducedMotion = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }

  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

const getConnectionReductionFlag = () => {
  if (typeof navigator === "undefined") {
    return false;
  }

  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  if (!connection) {
    return false;
  }

  if (connection.saveData) {
    return true;
  }

  const slowTypes = ["slow-2g", "2g", "3g"];
  return slowTypes.includes(connection.effectiveType);
};

export default function useReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(getPrefersReducedMotion);
  const [connectionFlag, setConnectionFlag] = useState(getConnectionReductionFlag);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    const handleChange = (event) => {
      setPrefersReducedMotion(event.matches);
    };

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
    } else if (typeof mediaQuery.addListener === "function") {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (typeof mediaQuery.removeEventListener === "function") {
        mediaQuery.removeEventListener("change", handleChange);
      } else if (typeof mediaQuery.removeListener === "function") {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof navigator === "undefined") {
      return undefined;
    }

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

    if (!connection || typeof connection.addEventListener !== "function") {
      return undefined;
    }

    const handleChange = () => {
      setConnectionFlag(getConnectionReductionFlag());
    };

    connection.addEventListener("change", handleChange);

    return () => {
      connection.removeEventListener("change", handleChange);
    };
  }, []);

  const reduceMotion = useMemo(
    () => prefersReducedMotion || connectionFlag,
    [prefersReducedMotion, connectionFlag]
  );

  return {
    reduceMotion,
    prefersReducedMotion,
    connectionFlag,
  };
}
